/**
 * Rich demo dataset for ContactRate managed-theater vision.
 * Same MPQ / Ava world as the design mock — shaped for API contracts.
 */

import type {
  ApiLead,
  ApiTeamMember,
  ApiTcpaRule,
  ApiThreadEvent,
  ApiUser,
  ApiWorkspace,
  LeadStage,
  LeadSource,
  ThreadEventKind,
} from '@contactrate/shared';
import type {
  ActivityItem,
  AttentionItem,
  DashboardStats,
  LiveState,
  SequenceListItem,
  WorkspaceSettings,
} from '../lib/apiHelpers.js';

const WS = 'ws_mpq_demo';
const NOW = '2026-07-07T16:35:00.000Z';

export const DEMO_USER: ApiUser = {
  id: 'user_dan',
  email: 'dan@mortgageprotectionquotes.com',
  role: 'client',
  workspaceId: WS,
  createdAt: '2026-06-01T00:00:00.000Z',
};

type RawLead = {
  id: string;
  first: string;
  last: string;
  phone: string;
  email: string;
  state: string;
  zip: string;
  stage: LeadStage;
  source: LeadSource;
  created: string;
  value: number;
  live?: boolean;
  dq?: boolean;
  needsYou?: boolean;
  unread?: boolean;
  lastTouch: string;
  cf: Record<string, string>;
  tel: { channel: string; line: string; carrier: string; device: string };
  seq: { name: string; day: number; step: number; total: number } | null;
};

const RAW_LEADS: RawLead[] = [
  { id: 'ld_01', first: 'Marcus', last: 'Bell', phone: '(512) 903-4412', email: 'mbell74@gmail.com', state: 'TX', zip: '78745', stage: 'booked', source: 'webhook', created: '2026-07-07T13:14:00.000Z', value: 2400, lastTouch: 'Ava · call · 12m ago', unread: true, cf: { age: 'Under 60', mortgage: '$150k – $200k' }, tel: { channel: 'imessage', line: 'Mobile', carrier: 'Verizon', device: 'iPhone' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 1, step: 4, total: 9 } },
  { id: 'ld_02', first: 'Denise', last: 'Okafor', phone: '(813) 224-9087', email: 'denise.okafor@yahoo.com', state: 'FL', zip: '33604', stage: 'transfer', source: 'facebook', created: '2026-07-07T14:02:00.000Z', value: 1850, live: true, lastTouch: 'Ava · on a call now', cf: { age: 'Under 60', mortgage: '$100k – $150k' }, tel: { channel: 'imessage', line: 'Mobile', carrier: 'AT&T', device: 'iPhone' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 1, step: 3, total: 9 } },
  { id: 'ld_03', first: 'Kim', last: 'Tran', phone: '(678) 440-2291', email: 'kimtran.ga@gmail.com', state: 'GA', zip: '30341', stage: 'contacted', source: 'webhook', created: '2026-07-06T19:20:00.000Z', value: 2100, needsYou: true, unread: true, lastTouch: 'Kim · SMS · 26m ago', cf: { age: 'Under 60', mortgage: '$200k – $250k' }, tel: { channel: 'imessage', line: 'Mobile', carrier: 'T-Mobile', device: 'iPhone' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 2, step: 5, total: 9 } },
  { id: 'ld_04', first: 'Ray', last: 'Delgado', phone: '(602) 771-3345', email: 'raydelgado@hotmail.com', state: 'AZ', zip: '85032', stage: 'closed', source: 'webhook', created: '2026-07-01T15:15:00.000Z', value: 3120, lastTouch: 'Dan · closed · Jul 3', cf: { age: '60 to 69', mortgage: '$50k – $100k' }, tel: { channel: 'sms', line: 'Mobile', carrier: 'Cricket', device: 'Android' }, seq: null },
  { id: 'ld_05', first: 'Linda', last: 'Osei', phone: '(704) 512-8830', email: 'linda.osei@outlook.com', state: 'NC', zip: '28216', stage: 'called', source: 'facebook', created: '2026-07-07T12:05:00.000Z', value: 1650, lastTouch: 'Ava · voicemail · 1h ago', cf: { age: 'Under 60', mortgage: '$150k – $200k' }, tel: { channel: 'rcs', line: 'Mobile', carrier: 'T-Mobile', device: 'Android' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 1, step: 3, total: 9 } },
  { id: 'ld_06', first: 'Jerome', last: 'Watkins', phone: '(470) 881-7720', email: 'jwatkins55@gmail.com', state: 'GA', zip: '30906', stage: 'follow_up', source: 'csv', created: '2026-07-05T14:41:00.000Z', value: 0, dq: true, lastTouch: 'Ava · DQ — renter · Jul 5', cf: { age: '60 to 69', mortgage: 'Renter — none' }, tel: { channel: 'sms', line: 'Mobile', carrier: 'Boost', device: 'Android' }, seq: null },
  { id: 'ld_07', first: 'Tamara', last: 'Nguyen', phone: '(305) 781-2240', email: 'tnguyen@icloud.com', state: 'FL', zip: '33101', stage: 'booked', source: 'facebook', created: '2026-07-06T15:12:00.000Z', value: 2400, lastTouch: 'Ava · booked Thu 10:30 AM', cf: { age: '60 to 69', mortgage: '$100k – $150k' }, tel: { channel: 'imessage', line: 'Mobile', carrier: 'Verizon', device: 'iPhone' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 2, step: 6, total: 9 } },
  { id: 'ld_08', first: 'Carlos', last: 'Estrada', phone: '(512) 334-1190', email: 'cestrada@gmail.com', state: 'TX', zip: '78201', stage: 'follow_up', source: 'webhook', created: '2026-07-05T16:08:00.000Z', value: 1700, lastTouch: 'Ava · SMS · yesterday', cf: { age: 'Under 60', mortgage: '$150k – $200k' }, tel: { channel: 'rcs', line: 'Mobile', carrier: 'Google Fi', device: 'Android' }, seq: { name: 'Aged Lead Revival', day: 2, step: 2, total: 6 } },
  { id: 'ld_09', first: 'James', last: 'Thornton', phone: '(832) 441-9021', email: 'jthornton@gmail.com', state: 'TX', zip: '75001', stage: 'new', source: 'webhook', created: '2026-07-07T15:56:00.000Z', value: 2000, lastTouch: 'Ava · first call queued', cf: { age: 'Under 60', mortgage: '$150k – $200k' }, tel: { channel: 'sms', line: 'Mobile', carrier: 'Verizon', device: 'Unknown' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 1, step: 1, total: 9 } },
  { id: 'ld_10', first: 'Sharon', last: 'Park', phone: '(678) 901-4453', email: 'sharon.park@yahoo.com', state: 'GA', zip: '31401', stage: 'closed', source: 'webhook', created: '2026-06-30T14:55:00.000Z', value: 1800, lastTouch: 'Dan · closed · Jul 3', cf: { age: 'Under 60', mortgage: '$100k – $150k' }, tel: { channel: 'imessage', line: 'Mobile', carrier: 'AT&T', device: 'iPhone' }, seq: null },
  { id: 'ld_11', first: 'Robert', last: 'Chavez', phone: '(602) 219-0032', email: 'rchavez@hotmail.com', state: 'AZ', zip: '85001', stage: 'contacted', source: 'webhook', created: '2026-07-06T13:30:00.000Z', value: 1450, unread: true, lastTouch: 'Robert · SMS · 3h ago', cf: { age: 'Under 60', mortgage: '$100k – $150k' }, tel: { channel: 'rcs', line: 'Mobile', carrier: 'T-Mobile', device: 'Android' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 2, step: 5, total: 9 } },
  { id: 'ld_12', first: 'Yvonne', last: 'Hollins', phone: '(214) 200-8833', email: 'yhollins@outlook.com', state: 'TX', zip: '75216', stage: 'new', source: 'facebook', created: '2026-07-07T16:12:00.000Z', value: 1900, lastTouch: 'Ava · first call queued', cf: { age: '60 to 69', mortgage: '$50k – $100k' }, tel: { channel: 'voice', line: 'Landline', carrier: 'Frontier', device: '—' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 1, step: 1, total: 9 } },
  { id: 'ld_13', first: 'Anthony', last: 'Mills', phone: '(912) 662-3310', email: 'amills@gmail.com', state: 'GA', zip: '31302', stage: 'presented', source: 'webhook', created: '2026-07-02T13:22:00.000Z', value: 2250, lastTouch: 'Katie · presented · Jul 6', cf: { age: 'Under 60', mortgage: '$200k – $250k' }, tel: { channel: 'rcs', line: 'Mobile', carrier: 'Verizon', device: 'Android' }, seq: null },
  { id: 'ld_17', first: 'Kevin', last: 'Adeyemi', phone: '(281) 993-4420', email: 'kadeyemi@gmail.com', state: 'TX', zip: '77001', stage: 'booked', source: 'facebook', created: '2026-07-05T13:55:00.000Z', value: 2050, lastTouch: 'Ava · booked today 4:00 PM', cf: { age: 'Under 60', mortgage: '$150k – $200k' }, tel: { channel: 'imessage', line: 'Mobile', carrier: 'AT&T', device: 'iPhone' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 3, step: 7, total: 9 } },
  { id: 'ld_20', first: 'Diane', last: 'Foster', phone: '(210) 555-0187', email: 'dfoster.tx@gmail.com', state: 'TX', zip: '78209', stage: 'new', source: 'webhook', created: '2026-07-07T16:31:00.000Z', value: 2200, lastTouch: 'Arrived 4m ago', cf: { age: 'Under 60', mortgage: '$200k – $250k' }, tel: { channel: 'imessage', line: 'Mobile', carrier: 'Verizon', device: 'iPhone' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 1, step: 1, total: 9 } },
  { id: 'ld_21', first: 'Hank', last: 'Willis', phone: '(512) 555-0161', email: 'hankwillis@gmail.com', state: 'TX', zip: '78613', stage: 'contacted', source: 'webhook', created: '2026-07-07T15:42:00.000Z', value: 1950, live: true, lastTouch: 'Ava · on a call now', cf: { age: 'Under 60', mortgage: '$150k – $200k' }, tel: { channel: 'rcs', line: 'Mobile', carrier: 'AT&T', device: 'Android' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 1, step: 2, total: 9 } },
  { id: 'ld_22', first: 'Rosa', last: 'Jimenez', phone: '(210) 555-0148', email: 'rosa.jimenez@yahoo.com', state: 'TX', zip: '78210', stage: 'called', source: 'facebook', created: '2026-07-07T16:20:00.000Z', value: 1750, live: true, lastTouch: 'Ava · on a call now', cf: { age: 'Under 60', mortgage: '$100k – $150k' }, tel: { channel: 'imessage', line: 'Mobile', carrier: 'T-Mobile', device: 'iPhone' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 1, step: 1, total: 9 } },
  { id: 'ld_25', first: 'Marcy', last: 'Cole', phone: '(786) 555-0129', email: 'marcycole@gmail.com', state: 'FL', zip: '33125', stage: 'contacted', source: 'facebook', created: '2026-07-07T13:50:00.000Z', value: 2600, live: true, lastTouch: 'Ava · on a call now', cf: { age: 'Under 60', mortgage: '$200k – $250k' }, tel: { channel: 'imessage', line: 'Mobile', carrier: 'AT&T', device: 'iPhone' }, seq: { name: 'Fresh Lead — Speed to Contact', day: 1, step: 4, total: 9 } },
];

function toApiLead(r: RawLead): ApiLead {
  const tags: string[] = [];
  if (r.dq) tags.push('dq');
  if (r.live) tags.push('live');
  if (r.seq) tags.push(r.seq.name);
  return {
    id: r.id,
    workspaceId: WS,
    firstName: r.first,
    lastName: r.last,
    phone: r.phone,
    email: r.email,
    state: r.state,
    zip: r.zip,
    source: r.source,
    stage: r.stage,
    dealValue: r.value,
    customFields: { ...r.cf, lastTouch: r.lastTouch },
    tags,
    telIntel: {
      channel: r.tel.channel as 'imessage' | 'rcs' | 'sms' | 'voice',
      line_type: (r.tel.line === 'Mobile' || r.tel.line === 'Landline' || r.tel.line === 'VoIP'
        ? r.tel.line
        : 'Unknown') as 'Mobile' | 'Landline' | 'VoIP' | 'Unknown',
      carrier: r.tel.carrier,
      device: r.tel.device,
    },
    aiPaused: false,
    dnc: !!r.dq,
    unread: !!r.unread,
    needsYou: !!r.needsYou || r.stage === 'transfer',
    createdAt: r.created,
    updatedAt: NOW,
  };
}

let leads: ApiLead[] = RAW_LEADS.map(toApiLead);

const THREAD_MAP: Record<string, ApiThreadEvent[]> = {
  ld_01: [
    ev('e1', 'ld_01', 'system', 'Lead received via webhook — enrolled in Fresh Lead', '2026-07-07T13:14:00.000Z'),
    ev('e2', 'ld_01', 'call', 'Attempt 1 — no answer', '2026-07-07T13:15:00.000Z', {
      outcome: 'no_answer', duration_s: 41, transcript: [],
    }),
    ev('e3', 'ld_01', 'ai', 'Hi Marcus — this is Ava from Dan’s office. You asked about protecting your mortgage for you and Linda…', '2026-07-07T13:17:00.000Z', undefined, 'up'),
    ev('e4', 'ld_01', 'in', 'Yeah I filled out the form last night. Who is this?', '2026-07-07T14:02:00.000Z'),
    ev('e5', 'ld_01', 'call', 'Connected — booked presentation', '2026-07-07T17:31:00.000Z', {
      outcome: 'connected', duration_s: 134, transcript: [
        { who: 'Ava', text: 'Marcus! Ava again with Mortgage Protection Quotes — good timing?' },
        { who: 'Marcus', text: 'Yeah I got a few minutes.' },
        { who: 'Ava', text: 'You both qualify for the no-exam plans. Tuesday at 2:30, Dan can walk you through it.' },
        { who: 'Marcus', text: 'Tuesday works.' },
      ],
    }, 'up'),
    ev('e6', 'ld_01', 'system', 'Ava booked Presentation — today 2:30 PM with Dan Reyes', '2026-07-07T17:33:00.000Z'),
  ],
  ld_02: [
    ev('e7', 'ld_02', 'system', 'Lead received via Facebook Lead Ads', '2026-07-07T14:02:00.000Z'),
    ev('e8', 'ld_02', 'ai', 'Hi Denise, Ava from Dan’s office — saw your request about coverage for your place in Tampa…', '2026-07-07T14:04:00.000Z'),
    ev('e9', 'ld_02', 'in', 'now is fine', '2026-07-07T16:29:00.000Z'),
    ev('e10', 'ld_02', 'call', 'LIVE — transfer ready', '2026-07-07T16:31:00.000Z', {
      outcome: 'connected', duration_s: 84, phase: 'Live transfer', sentiment: 'positive',
      transcript: [
        { who: 'Ava', text: '…you pre-qualify for the simplified options. I have Dan available right now, want me to bring him on?' },
        { who: 'Denise', text: 'Yes, please.' },
      ],
    }),
  ],
  ld_03: [
    ev('e11', 'ld_03', 'system', 'Lead received via webhook', '2026-07-06T19:20:00.000Z'),
    ev('e12', 'ld_03', 'call', 'Voicemail left', '2026-07-06T19:22:00.000Z', { outcome: 'no_answer', duration_s: 38, transcript: [] }),
    ev('e13', 'ld_03', 'in', 'Text is fine. Also — can this cover my mother? She’s 82…', '2026-07-06T23:44:00.000Z'),
    ev('e14', 'ld_03', 'in', '?? also is the rate locked or does it go up', '2026-07-07T16:09:00.000Z'),
    ev('e15', 'ld_03', 'system', 'Ava flagged for review — guaranteed-issue at 82 is outside script', '2026-07-07T16:09:00.000Z'),
  ],
};

function ev(
  id: string,
  leadId: string,
  kind: ThreadEventKind,
  text: string,
  createdAt: string,
  payload?: Record<string, unknown>,
  feedback: 'up' | 'down' | null = null,
): ApiThreadEvent {
  return {
    id,
    leadId,
    kind,
    body: text,
    payload: payload ?? null,
    feedback,
    createdAt,
  };
}

export const DEMO_SETTINGS: ApiWorkspace & { settings?: WorkspaceSettings } = {
  id: WS,
  name: 'Mortgage Protection Quotes',
  vertical: 'mortgage_protection',
  plan: 'growth',
  health: 'good',
  timezone: 'America/Chicago',
  aiName: 'Ava',
  defaultGoal: 'transfer',
  createdAt: '2026-06-01T00:00:00.000Z',
  settings: {
    must_know: [
      'Confirm homeowner status',
      'Mortgage balance range',
      'Age band under 70 for simplified issue',
      'Availability for live transfer to Dan',
    ],
    nice_to_know: ['Spouse on title', 'Preferred call window', 'How they heard about us'],
    hard_dq: ['Not a homeowner / renter only', 'Age 80+', 'Do-not-call request'],
    notifications: {
      live_transfer: true,
      appointment_booked: true,
      needs_attention: true,
      daily_digest: false,
    },
  },
};

export const DEMO_TEAM: ApiTeamMember[] = [
  {
    id: 'tm_1',
    workspaceId: WS,
    name: 'Dan Reyes',
    role: 'Owner / Closer',
    phone: '(512) 555-0199',
    receives: 'transfers_and_appts',
    available: true,
    transferHours: { monday: { start: '09:00', end: '18:00' }, tuesday: { start: '09:00', end: '18:00' } },
  },
  {
    id: 'tm_2',
    workspaceId: WS,
    name: 'Katie Muñoz',
    role: 'Closer',
    phone: '(512) 555-0188',
    receives: 'transfers_and_appts',
    available: true,
    transferHours: null,
  },
];

export const DEMO_TCPA: ApiTcpaRule[] = [
  {
    id: 'tcpa_1',
    region: 'Federal',
    windowStart: '08:00',
    windowEnd: '21:00',
    basis: 'lead_local',
    note: 'No outbound voice outside 8am–9pm lead-local',
    enforced: true,
    locked: true,
    overrideLog: [],
  },
  {
    id: 'tcpa_2',
    region: 'Workspace',
    windowStart: '08:00',
    windowEnd: '21:00',
    basis: 'consent',
    note: 'Require recorded consent before first dial',
    enforced: true,
    locked: true,
    overrideLog: [],
  },
  {
    id: 'tcpa_3',
    region: 'Workspace',
    windowStart: '00:00',
    windowEnd: '23:59',
    basis: 'frequency',
    note: 'Max 3 voice attempts per lead per local day',
    enforced: true,
    locked: true,
    overrideLog: [],
  },
];

export const DEMO_SEQUENCES: SequenceListItem[] = [
  {
    id: 's1',
    name: 'Fresh Lead — Speed to Contact',
    goal: 'transfer',
    active: true,
    trigger: 'Lead received via webhook or Facebook Lead Ads',
    enrolled: 47,
    connectRate: 38,
    bookedRate: 12,
    exitDays: 14,
    stepCount: 9,
  },
  {
    id: 's2',
    name: 'Aged Lead Revival',
    goal: 'book',
    active: true,
    trigger: 'Lead age > 7 days, no transfer or book',
    enrolled: 18,
    connectRate: 22,
    bookedRate: 8,
    exitDays: 9,
    stepCount: 6,
  },
  {
    id: 's3',
    name: 'Missed Transfer Follow-up',
    goal: 'book',
    active: true,
    trigger: 'Live transfer attempted, closer no-answer',
    enrolled: 6,
    connectRate: 61,
    bookedRate: 44,
    exitDays: 3,
    stepCount: 4,
  },
];

export function getDashboardStats(range: string): DashboardStats {
  const mult = range === 'today' ? 1 : range === '7d' ? 5.2 : 18;
  return {
    range,
    newLeads: Math.round(12 * mult),
    contacts: Math.round(5 * mult),
    contactRate: 41,
    avgTouchesToContact: 2.4,
    bookRate: 9,
    liveTransfers: Math.round(2 * mult),
    dials: Math.round(28 * mult),
    answered: Math.round(11 * mult),
    answerRate: 39,
    avgCallLengthS: 118,
    avgCallsToContact: 2.1,
    smsSent: Math.round(34 * mult),
    smsReplies: Math.round(9 * mult),
    replyRate: 26,
    firstReplyMedianS: 420,
    optOuts: Math.round(0.4 * mult),
  };
}

export function getLiveState(): LiveState {
  const live = leads.filter((l) => l.tags.includes('live') || l.stage === 'transfer').slice(0, 3);
  return {
    liveCalls: live.map((l, i) => ({
      leadId: l.id,
      leadName: `${l.firstName} ${l.lastName}`,
      initials: `${l.firstName[0]}${l.lastName[0]}`,
      state: l.state ?? '—',
      attemptNo: 1,
      phase: l.stage === 'transfer' ? 'Live transfer' : 'Qualifying',
      sentiment: 'positive' as const,
      durationS: 84 + i * 23,
      transcript:
        l.id === 'ld_02'
          ? [
              { who: 'Ava', text: '…you pre-qualify for the simplified options. I have Dan available right now, want me to bring him on?' },
              { who: 'Denise', text: 'Yes, please.' },
            ]
          : [
              { who: 'Ava', text: 'Hi — it’s Ava from Dan’s office. You asked about mortgage protection…' },
              { who: l.firstName, text: 'Yeah, I filled something out online.' },
            ],
      transferReady: l.stage === 'transfer',
    })),
    queueCount: 14,
    nextDialIn: '0:42',
    quietHoursEnforced: true,
    queuePaused: false,
  };
}

export function getActivity(): ActivityItem[] {
  return [
    { id: 'a1', text: 'Ava live-transferred Denise Okafor to Dan', ts: '2026-07-07T16:33:00.000Z', kind: 'transfer' },
    { id: 'a2', text: 'Marcus Bell booked for today 2:30 PM', ts: '2026-07-07T17:33:00.000Z', kind: 'book' },
    { id: 'a3', text: 'Diane Foster arrived — enrolled in Fresh Lead', ts: '2026-07-07T16:31:00.000Z', kind: 'lead' },
    { id: 'a4', text: 'Ava left voicemail for Linda Osei', ts: '2026-07-07T15:15:00.000Z', kind: 'call' },
    { id: 'a5', text: 'Kim Tran replied on iMessage — flagged for review', ts: '2026-07-07T16:09:00.000Z', kind: 'attention' },
    { id: 'a6', text: 'Tamara Nguyen booked Thu 10:30 AM with Katie', ts: '2026-07-06T22:14:00.000Z', kind: 'book' },
  ];
}

export function getAttention(): AttentionItem[] {
  return [
    {
      id: 'att1',
      leadId: 'ld_02',
      leadName: 'Denise Okafor',
      text: 'Live transfer ringing your phone — answer now',
      action: 'Answer transfer',
      ts: '2026-07-07T16:32:00.000Z',
    },
    {
      id: 'att2',
      leadId: 'ld_01',
      leadName: 'Marcus Bell',
      text: 'Presentation in 55 minutes · Ava prepped both options',
      action: 'View appointment',
      ts: '2026-07-07T16:20:00.000Z',
    },
    {
      id: 'att3',
      leadId: 'ld_03',
      leadName: 'Kim Tran',
      text: 'Out-of-script question (mother 82) — ContactRate reviewing',
      action: 'View thread',
      ts: '2026-07-07T16:09:00.000Z',
    },
  ];
}

export function listLeads(q: {
  stage?: string;
  source?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}) {
  let rows = [...leads];
  if (q.stage) rows = rows.filter((l) => l.stage === q.stage);
  if (q.source) rows = rows.filter((l) => l.source === q.source);
  if (q.search) {
    const s = q.search.toLowerCase();
    rows = rows.filter(
      (l) =>
        l.firstName.toLowerCase().includes(s) ||
        l.lastName.toLowerCase().includes(s) ||
        l.phone.includes(s) ||
        (l.email ?? '').toLowerCase().includes(s),
    );
  }
  const page = q.page ?? 1;
  const pageSize = q.pageSize ?? 50;
  const start = (page - 1) * pageSize;
  const data = rows.slice(start, start + pageSize);
  return {
    data,
    total: rows.length,
    page,
    pageSize,
    hasMore: start + pageSize < rows.length,
  };
}

export function getLead(id: string) {
  const lead = leads.find((l) => l.id === id);
  if (!lead) return null;
  return {
    ...lead,
    threadEvents: THREAD_MAP[id] ?? [
      ev(`sys-${id}`, id, 'system', 'Lead enrolled — Ava is working this contact', lead.createdAt),
      ev(`touch-${id}`, id, 'ai', `Hi ${lead.firstName} — Ava from Dan’s office…`, lead.updatedAt),
    ],
  };
}

export function patchLeadLocal(id: string, body: Record<string, unknown>) {
  leads = leads.map((l) => {
    if (l.id !== id) return l;
    return {
      ...l,
      ...(body.stage ? { stage: body.stage as LeadStage } : {}),
      ...(body.aiPaused !== undefined ? { aiPaused: !!body.aiPaused } : {}),
      ...(body.tags ? { tags: body.tags as string[] } : {}),
      ...(body.needsYou !== undefined ? { needsYou: !!body.needsYou } : {}),
      updatedAt: new Date().toISOString(),
    };
  });
  return leads.find((l) => l.id === id)!;
}

export function getSettings() {
  return { ...DEMO_SETTINGS };
}

export function getTeam() {
  return DEMO_TEAM;
}

export function getTcpa() {
  return DEMO_TCPA;
}

export function getSequences() {
  return DEMO_SEQUENCES;
}
